import { OTP_VERIFIED_LOCK_KEY } from 'components/onboardingLayout';

import { getUserIdToken } from 'services/auth';

const COGNITO_USER_ALREADY_EXISTS_ERROR = 'cognito user already exists with username';
const COGNITO_USER_ALREADY_EXISTS_GUIDANCE =
  'Please go to your email and continue journey with different email.';

/** User-facing copy when OTP verification fails for an incorrect/invalid code. */
export const INCORRECT_OTP_ERROR_MESSAGE = 'Incorrect OTP, Please try again';

/** Shown after OTP attempt limit / invalid otp_sent state (referral onboarding recovery). */
export const OTP_ATTEMPT_LIMIT_RECOVERY_MESSAGE =
  "You've reached the verification attempt limit. Please restart your journey using the link from the email template.";

const AXIOS_NETWORK_ERROR_MESSAGE = 'network error';

export type OtpOverlayLoaderType = 'session' | 'sign-in' | null;

type GetOtpErrorMessageOptions = {
  withCognitoUserExistsGuidance?: boolean;
};

function withKnownOtpErrorGuidance(message: string): string {
  if (!message.toLowerCase().includes(COGNITO_USER_ALREADY_EXISTS_ERROR)) {
    return message;
  }

  return `${message}\n${COGNITO_USER_ALREADY_EXISTS_GUIDANCE}`;
}

function isOtpAttemptLimitOrInvalidStateMessage(lower: string): boolean {
  if (lower.includes('otp_sent')) {
    return true;
  }

  if (lower.includes('attempt limit') || lower.includes('attempts limit')) {
    return true;
  }

  if (lower.includes('too many') && lower.includes('attempt')) {
    return true;
  }

  if (lower.includes('max') && lower.includes('attempt') && lower.includes('otp')) {
    return true;
  }

  return false;
}

/** Maps axios/API OTP failure text to approved user-facing copy. */
function normalizeIncorrectOtpMessage(message: string): string {
  const trimmed = message.trim();
  const lower = trimmed.toLowerCase();

  if (lower === AXIOS_NETWORK_ERROR_MESSAGE) {
    return INCORRECT_OTP_ERROR_MESSAGE;
  }

  if (isOtpAttemptLimitOrInvalidStateMessage(lower)) {
    return OTP_ATTEMPT_LIMIT_RECOVERY_MESSAGE;
  }

  if (
    lower.includes('invalid or expired') ||
    lower.includes('invalid code') ||
    lower.includes('invalid otp') ||
    lower.includes('incorrect otp') ||
    lower.includes('code mismatch')
  ) {
    return INCORRECT_OTP_ERROR_MESSAGE;
  }

  return trimmed;
}

export function lockOnboardingAfterOtpVerification(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(OTP_VERIFIED_LOCK_KEY, 'true');
}

export function getEuAuthHeaders(): HeadersInit {
  const idToken = getUserIdToken();

  return {
    'Content-Type': 'application/json',
    Authorization: idToken ? `Bearer ${idToken}` : 'Bearer ',
  };
}

export function getOtpErrorMessage(error: unknown, options?: GetOtpErrorMessageOptions): string {
  if (error instanceof Error && error.message.trim()) {
    const normalized = normalizeIncorrectOtpMessage(error.message);

    if (options?.withCognitoUserExistsGuidance) {
      return withKnownOtpErrorGuidance(normalized);
    }

    return normalized;
  }

  return INCORRECT_OTP_ERROR_MESSAGE;
}

export function getVerifyButtonLabel(isLoading: boolean, isSubmitAllowed: boolean): string {
  if (isLoading) {
    return 'Verifying...';
  }

  if (!isSubmitAllowed) {
    return 'Preparing...';
  }

  return 'Verify';
}

export function resolveOtpOverlayLoaderType(
  isRecreatingSession: boolean,
  showSignInTransitionLoader: boolean
): OtpOverlayLoaderType {
  if (isRecreatingSession) {
    return 'session';
  }

  if (showSignInTransitionLoader) {
    return 'sign-in';
  }

  return null;
}
